import { FareOption } from "@/core/types/flight";
import { ROUTE_TYPE } from "@/core/enums/ROUTE_TYPE";
import { ICoreResponse } from "@/core/interfaces/coreResponse.interface";
import type { IconName } from "@/types/proxima-design-system";
import { FlightServiceFee } from "@/core/interfaces/IFlightSearch.interface";

export interface APIResponse<T> extends ICoreResponse {
  success: boolean;
  status: number;
  data: {
    data: T;
  };
}

export interface PollingData {
  keep_polling: boolean;
}

export type PollingCallback<T, Q> = (query: Q) => Promise<APIResponse<T>>;
export type PollingEmitCallback<T> = (results: T | null) => void;
export type BookingInitiatedCallback = (
  bookingId: string | null,
  currencyCode?: string | undefined,
) => void;

export interface DateOption {
  departureDate: string;
  arrivalDate?: string;
  active: boolean;
}

export enum AmountType {
  FIXED = "FIXED",
  PERCENTAGE = "PERCENTAGE",
}

export enum FareType {
  FIXED = "FIXED",
  GROSS = "GROSS",
}

export enum ListViewType {
  SINGLE = "SINGLE",
  PAIR = "PAIR",
}

export interface QueryData {
  route_type: ROUTE_TYPE;
  adult: string;
  child: string;
  infant: string;
  cabin_class: string;
  trips: string;
  fp?: string;
  pollId?: string;
  selected_airlines: string;
  service_fees?: string | number;
  pricing_type?: string;
  apply_on_gross_fare?: boolean;
  currency_code?: string;
}

export interface QueryLeg {
  origin: string;
  destination: string;
  departure_date: string;
  active?: boolean;
}

export interface SelectedQueryLeg {
  origin: string;
  destination: string;
  index: number;
}

export interface QueryType {
  route_type: ROUTE_TYPE;
  traveler_count: {
    adult_count: number;
    child_count: number;
    infant_count: number;
  };
  cabin_class: string;
  non_stop_flight: boolean;
  origin?: string;
  destination?: string;
  departure_date?: string;
  return_date?: string | null;
  legs?: QueryLeg[];
  pricing: FlightServiceFee | null;
  financial_profile_id?: string;
  poll_id: string | null;
  full_result: boolean;
  selected_airlines: string[] | [];
  currency_code?: string;
  traveler_ids?: string[];
}

export enum FilterType {
  PRICE = "PRICE",
  STOPS = "STOPS",
  TIMES = "TIMES",
  AIRLINES = "AIRLINES",
  LAYOVER_TIMES = "LAYOVER_TIMES",
  FLIGHT_DURATION = "FLIGHT_DURATION",
  REFUNDABLE = "REFUNDABLE",
  BAGGAGE = "BAGGAGE",
  PREFERRED_AIRLINES = "PREFERRED_AIRLINES",
  OUT_OF_POLICY = "OUT_OF_POLICY",
}

export interface Filter {
  label: string;
  value: boolean;
  count: number;
  type: FilterType;
  icon?: IconName;
  preferred?: boolean;
}

export enum SortBy {
  PRICE = "price",
  DEPARTURE_TIME = "departure_time",
  DURATION = "duration",
  PREFERRED_FIRST = "preferred_first",
}

export enum SortOrder {
  ASC = "asc",
  DESC = "desc",
}

export interface SortOption {
  label: string;
  value: SortSelection;
}

export enum RefundableFilterOptions {
  ALL = "ALL",
  REFUNDABLE = "REFUNDABLE",
  NON_REFUNDABLE = "NON_REFUNDABLE",
}

export enum BaggageFilterOptions {
  ALL = "ALL",
  WITH_BAGGAGE = "WITH_BAGGAGE",
  WITHOUT_BAGGAGE = "WITHOUT_BAGGAGE",
}

export const SEARCH_RESULT_SORT_OPTIONS = [
  {
    label: "Price - Low to High",
    value: { sortBy: SortBy.PRICE, sortOrder: SortOrder.ASC },
  },
  {
    label: "Price - High to Low",
    value: { sortBy: SortBy.PRICE, sortOrder: SortOrder.DESC },
  },
  {
    label: "Departure Time - Early to Late",
    value: { sortBy: SortBy.DEPARTURE_TIME, sortOrder: SortOrder.ASC },
  },
  {
    label: "Departure Time - Late to Early",
    value: { sortBy: SortBy.DEPARTURE_TIME, sortOrder: SortOrder.DESC },
  },
  {
    label: "Duration - Shortest to Longest",
    value: { sortBy: SortBy.DURATION, sortOrder: SortOrder.ASC },
  },
  {
    label: "Duration - Longest to Shortest",
    value: { sortBy: SortBy.DURATION, sortOrder: SortOrder.DESC },
  },
] as SortOption[];

export const DEFAULT_SORT_OPTION = {
  label: "Price - Low to High",
  value: { sortBy: SortBy.PRICE, sortOrder: SortOrder.ASC },
};

export const PREFERRED_FIRST_SORT_OPTION: SortOption = {
  label: "Preferred First",
  value: { sortBy: SortBy.PREFERRED_FIRST, sortOrder: SortOrder.ASC },
};

export interface SortSelection {
  sortBy: SortBy;
  sortOrder: SortOrder;
}

export interface RouteDetails {
  originCode: string;
  originTime: string;

  destinationCode: string;
  destinationTime: string;

  duration: string;

  stops: string[];
}

export interface SelectedFlight {
  logo: string;
  departureTime: string;
  arrivalTime: string;
}

export interface PolicyRule {
  rule: string;
  value: string;
}

export interface FlightRulesResponse {
  cancellation_policy: {
    policy_rules: PolicyRule[];
  };
  modification_policy: {
    policy_rules: PolicyRule[];
  };
  baggage_policy: {
    policy_rules: PolicyRule[];
  };
}

export interface TransformedFareOption extends FareOption {
  showInfo: boolean;
}

export interface TemperFare {
  amount: number;
  amountType: AmountType;
  fareType: FareType;
}

export interface BreakPoint {
  isDesktop: boolean;
  isLaptop: boolean;
  isTablet: boolean;
  isMobile: boolean;
}
